import React, { useState } from 'react';
import { Venue, VenuePackage } from '../../types';
import { ConicGlowCard } from '../common/ConicGlowCard';
import {
  CalendarIcon,
  ClockIcon,
  UsersIcon,
  SparklesIcon,
  ShieldCheckIcon,
  CheckIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  RadioDotIcon
} from '../common/Icons';

interface AvailabilityCalculatorDeckProps {
  venue?: Venue | null;
  initialPackage?: VenuePackage | null;
  onProceedToConfirmation: (config: {
    venue: Venue;
    selectedPackage: VenuePackage;
    guestCount: number;
    eventDate: string;
    slot: 'lunch' | 'dinner';
    totalEstimatedPkr: number;
  }) => void;
  onBack?: () => void;
  onTouchAction?: (action: string, data?: any) => void;
}

export const AvailabilityCalculatorDeck: React.FC<AvailabilityCalculatorDeckProps> = ({
  venue,
  initialPackage,
  onProceedToConfirmation,
  onBack,
  onTouchAction
}) => {
  const defaultVenue: Venue = {
    id: 'v-karsaz-01',
    slug: 'majestic-grand-banquet-karsaz',
    name: 'Majestic Grand Banquet',
    area: 'Karsaz',
    address: 'Main Karsaz Road, Karachi',
    lat: 24.8821,
    lng: 67.0892,
    venueType: 'banquet_hall',
    minCapacity: 250,
    maxCapacity: 800,
    googleRating: 4.8,
    googleReviewCount: 342,
    isClaimed: true,
    packages: [
      {
        id: 'pkg-flat-peak',
        packageType: 'flat_rental_peak',
        name: 'Peak Season Hall Rental',
        pricePkr: 330000,
        minGuests: 250,
        menuItems: ['Main Hall & Stage Setup', 'Central Air Conditioning & Lighting', 'Standby Generator Backup', 'Valet Parking & Bridal Suite']
      },
      {
        id: 'pkg-flat-offpeak',
        packageType: 'flat_rental_offpeak',
        name: 'Off-Peak / Weekday Hall Rental',
        pricePkr: 240000,
        minGuests: 200,
        menuItems: ['Weekday / Off-Season Hall Rate', 'Central Air Conditioning & Lighting', 'Standby Generator Backup', 'Valet Parking & Bridal Suite']
      }
    ]
  };

  const currentVenue = venue || defaultVenue;
  const rawPackages = currentVenue.packages && currentVenue.packages.length > 0
    ? currentVenue.packages
    : defaultVenue.packages!;
  const uniqueRaw = Array.from(
    new Map(rawPackages.map((p) => [`${p.packageType}:${p.name}`, p])).values()
  );

  const flatPkg =
    uniqueRaw.find((p) => !p.packageType.includes('offpeak') && (p.packageType.includes('flat') || p.pricePkr >= 50000)) ||
    uniqueRaw.find((p) => p.packageType.includes('flat') || p.pricePkr >= 50000);
  const offPeakPkg = uniqueRaw.find((p) => p.packageType.includes('offpeak'));
  const baseBenchmark = uniqueRaw[0] && uniqueRaw[0].pricePkr < 25000 ? uniqueRaw[0].pricePkr : 1850;
  const peakRentalPkr = flatPkg ? Number(flatPkg.pricePkr) : Math.round(baseBenchmark * 180);
  const offPeakRentalPkr =
    offPeakPkg && Number(offPeakPkg.pricePkr) > 0 && Number(offPeakPkg.pricePkr) < peakRentalPkr
      ? Number(offPeakPkg.pricePkr)
      : Math.max(85000, Math.round((peakRentalPkr * 0.72) / 5000) * 5000);

  const packages: VenuePackage[] = [
    {
      id: flatPkg?.id || `${currentVenue.id}-peak-rental`,
      packageType: 'flat_rental_peak',
      name: 'Peak Season Hall Rental',
      pricePkr: peakRentalPkr,
      minGuests: currentVenue.minCapacity || 200,
      menuItems: [
        'Main Hall & Stage Platform',
        'Central Air Conditioning & Lighting',
        'Standby Generator Backup',
        'Valet Parking & VIP Lounge'
      ]
    },
    {
      id: offPeakPkg?.id || `${currentVenue.id}-offpeak-rental`,
      packageType: 'flat_rental_offpeak',
      name: 'Off-Peak / Weekday Hall Rental',
      pricePkr: offPeakRentalPkr,
      minGuests: currentVenue.minCapacity || 150,
      menuItems: [
        'Weekday / Off-Season Hall Rate',
        'Central Air Conditioning & Lighting',
        'Standby Generator Backup',
        'Valet Parking & VIP Lounge'
      ]
    }
  ];

  const [selectedPkg, setSelectedPkg] = useState<VenuePackage>(() => {
    if (initialPackage && initialPackage.pricePkr >= 50000) {
      const matched = packages.find((p) => p.id === initialPackage.id || p.name === initialPackage.name);
      if (matched) return matched;
    }
    return packages[0];
  });
  const [guestCount, setGuestCount] = useState<number>(400);
  const [eventDate, setEventDate] = useState<string>('2026-11-25');
  const [selectedSlot, setSelectedSlot] = useState<'lunch' | 'dinner'>('dinner');
  const [addonFloralStage, setAddonFloralStage] = useState<boolean>(true);
  const [addonValetTeam, setAddonValetTeam] = useState<boolean>(false);
  const [addonSoundLight, setAddonSoundLight] = useState<boolean>(false);

  // Upcoming event dates matrix
  const upcomingDates = [
    { date: '2026-11-21', label: 'Sat, 21 Nov', lunch: 'available', dinner: 'held' },
    { date: '2026-11-22', label: 'Sun, 22 Nov', lunch: 'available', dinner: 'available' },
    { date: '2026-11-25', label: 'Wed, 25 Nov', lunch: 'available', dinner: 'available' },
    { date: '2026-11-27', label: 'Fri, 27 Nov', lunch: 'available', dinner: 'available' },
    { date: '2026-11-28', label: 'Sat, 28 Nov', lunch: 'held', dinner: 'available' },
    { date: '2026-11-29', label: 'Sun, 29 Nov', lunch: 'available', dinner: 'available' }
  ];

  // Event shift definitions
  const slotsConfig = [
    {
      id: 'lunch' as const,
      name: 'Daytime / Lunch Shift',
      timeWindow: '1:00 PM – 5:00 PM',
      status: 'available',
      buttonId: 'slot-select-lunch'
    },
    {
      id: 'dinner' as const,
      name: 'Evening / Dinner Shift',
      timeWindow: '7:30 PM – 11:30 PM',
      status: 'available',
      buttonId: 'slot-select-dinner'
    }
  ];

  const handleGuestChange = (val: number) => {
    setGuestCount(val);
    if (onTouchAction) {
      onTouchAction('adjust_guests', { guestCount: val, venueId: currentVenue.id });
    }
  };

  const handleSlotSelect = (slot: 'lunch' | 'dinner') => {
    setSelectedSlot(slot);
    if (onTouchAction) {
      onTouchAction('select_slot', { slot, date: eventDate, venueId: currentVenue.id });
    }
  };

  const handlePackageSelect = (pkg: VenuePackage) => {
    setSelectedPkg(pkg);
    if (onTouchAction) {
      onTouchAction('select_package', { packageId: pkg.id, rate: pkg.pricePkr });
    }
  };

  // Pure Hall Rental + Add-on Calculations
  const flatRental = selectedPkg.pricePkr;
  const addonsTotal =
    (addonFloralStage ? 55000 : 0) +
    (addonValetTeam ? 25000 : 0) +
    (addonSoundLight ? 35000 : 0);
  const totalEstimatedPkr = flatRental + addonsTotal;

  const isBelowMin = guestCount < currentVenue.minCapacity;
  const isAboveMax = guestCount > currentVenue.maxCapacity;

  const handleProceed = () => {
    onProceedToConfirmation({
      venue: currentVenue,
      selectedPackage: selectedPkg,
      guestCount,
      eventDate,
      slot: selectedSlot,
      totalEstimatedPkr
    });
  };

  return (
    <div className="stage-deck-content">
      <div className="deck-header" style={{ marginBottom: '0.45rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', minWidth: 0, width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.45rem', flexWrap: 'nowrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', minWidth: 0 }}>
              {onBack && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onBack}
                  style={{ padding: '0.28rem 0.58rem', fontSize: '0.71rem', gap: '0.28rem', flexShrink: 0 }}
                >
                  <ArrowLeftIcon size={12} />
                  <span>Back</span>
                </button>
              )}
              <h2 className="deck-title" style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Slot &amp; Rental Calculator
              </h2>
            </div>
            <span className="pill-badge verified" style={{ fontSize: '0.62rem', padding: '0.12rem 0.45rem', flexShrink: 0, maxWidth: '10rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              <ShieldCheckIcon size={10} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentVenue.name}</span>
            </span>
          </div>
          <p className="deck-subtitle header-mobile-hide" style={{ margin: 0 }}>
            Configure event date, shift window, guest capacity, and hall rental tier for <strong>{currentVenue.name}</strong> ({currentVenue.area})
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(19rem, 100%), 1fr))', gap: '0.55rem' }}>
        {/* Left Column: Date Matrix, Slot Selection & Guest Count */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
          <div className="glass-panel" style={{ padding: '0.65rem 0.85rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.45rem', marginBottom: '0.42rem' }}>
              <h3 style={{ fontSize: '0.82rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <CalendarIcon size={14} />
                <span>1. Event Date &amp; Shift</span>
              </h3>
              <input
                type="date"
                aria-label="Custom Calendar Date"
                value={eventDate}
                onChange={(e) => {
                  setEventDate(e.target.value);
                  if (onTouchAction) onTouchAction('change_date', { date: e.target.value });
                }}
                className="form-input"
                style={{ width: 'auto', padding: '0.24rem 0.48rem', fontSize: '0.72rem', height: '1.85rem' }}
              />
            </div>

            {/* Quick-Tap Event Date Strip */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.3rem', marginBottom: '0.45rem' }}>
              {upcomingDates.map((d) => {
                const isSelected = eventDate === d.date;
                return (
                  <button
                    key={d.date}
                    type="button"
                    onClick={() => {
                      setEventDate(d.date);
                      if (onTouchAction) onTouchAction('change_date', { date: d.date });
                    }}
                    style={{
                      padding: '0.28rem 0.25rem',
                      borderRadius: '0.5rem',
                      border: isSelected
                        ? '1px solid hsl(var(--glow-gemini-blue))'
                        : '1px solid hsl(var(--surface-border) / 0.6)',
                      backgroundColor: isSelected
                        ? 'hsl(var(--glow-gemini-blue) / 0.16)'
                        : 'hsl(var(--surface-elevated) / 0.6)',
                      color: 'hsl(var(--text-primary))',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '0.67rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{d.label}</div>
                    <div style={{ fontSize: '0.56rem', color: 'hsl(var(--accent-emerald))', marginTop: '0.04rem', display: 'inline-flex', alignItems: 'center', gap: '0.18rem' }}>
                      <RadioDotIcon size={8} />
                      <span>Open</span>
                    </div>
                  </button>
                );
              })}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.4rem' }}>
              {slotsConfig.map((s) => (
                <button
                  key={s.id}
                  id={s.buttonId}
                  type="button"
                  className="glass-panel"
                  onClick={() => handleSlotSelect(s.id)}
                  style={{
                    padding: '0.42rem 0.55rem',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: '0.14rem',
                    borderRadius: '0.65rem',
                    border: selectedSlot === s.id
                      ? '1px solid hsl(var(--glow-gemini-blue))'
                      : '1px solid hsl(var(--surface-border) / 0.5)',
                    backgroundColor: selectedSlot === s.id ? 'hsl(var(--glow-gemini-blue) / 0.14)' : 'hsl(var(--surface-card) / 0.7)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '0.2rem' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.73rem', color: 'hsl(var(--text-primary))', display: 'flex', alignItems: 'center', gap: '0.25rem', minWidth: 0 }}>
                      <ClockIcon size={12} />
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.id === 'lunch' ? 'Lunch Shift' : 'Dinner Shift'}
                      </span>
                    </div>
                    <span className="pill-badge verified" style={{ fontSize: '0.55rem', padding: '0.08rem 0.34rem' }}>
                      Open
                    </span>
                  </div>
                  <div style={{ fontSize: '0.64rem', color: 'hsl(var(--text-muted))' }}>
                    {s.timeWindow}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Guest Count Slider */}
          <div className="glass-panel" style={{ padding: '0.58rem 0.85rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.2rem' }}>
              <h3 style={{ fontSize: '0.82rem', fontWeight: 800, margin: 0, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <UsersIcon size={14} />
                <span>2. Expected Guest Capacity</span>
              </h3>
              <span className="num-tabular" style={{ fontSize: '0.94rem', fontWeight: 800, color: 'hsl(var(--accent-gold))' }}>
                {guestCount} Guests
              </span>
            </div>

            <input
              type="range"
              min="100"
              max="1200"
              step="25"
              value={guestCount}
              onChange={(e) => handleGuestChange(Number(e.target.value))}
              className="range-slider"
              style={{ margin: '0.32rem 0' }}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.66rem', color: 'hsl(var(--text-muted))' }}>
              <span>Min: {currentVenue.minCapacity} pax</span>
              <span>Max: {currentVenue.maxCapacity} pax</span>
            </div>

            {isBelowMin && (
              <div
                style={{
                  marginTop: '0.35rem',
                  padding: '0.32rem 0.55rem',
                  borderRadius: '0.45rem',
                  backgroundColor: 'hsl(var(--warning) / 0.15)',
                  border: '1px solid hsl(var(--warning) / 0.4)',
                  color: 'hsl(var(--warning))',
                  fontSize: '0.66rem'
                }}
              >
                Below min capacity ({currentVenue.minCapacity} pax). Flat hall rental applies.
              </div>
            )}

            {isAboveMax && (
              <div
                style={{
                  marginTop: '0.35rem',
                  padding: '0.32rem 0.55rem',
                  borderRadius: '0.45rem',
                  backgroundColor: 'hsl(var(--danger) / 0.15)',
                  border: '1px solid hsl(var(--danger) / 0.4)',
                  color: 'hsl(var(--danger))',
                  fontSize: '0.66rem'
                }}
              >
                Exceeds single hall capacity ({currentVenue.maxCapacity} pax).
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Hall Rental Tier Selection, Add-ons & Live Quotation Summary */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
          <div className="glass-panel" style={{ padding: '0.65rem 0.85rem' }}>
            <h3 style={{ fontSize: '0.82rem', fontWeight: 800, marginBottom: '0.42rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <SparklesIcon size={14} />
              <span>3. Hall Rental Tier &amp; Optional Add-Ons</span>
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.4rem', marginBottom: '0.48rem' }}>
              {packages.map((pkg) => {
                const isSelected = selectedPkg.id === pkg.id;
                const isPeak = !pkg.packageType.includes('offpeak');
                return (
                  <button
                    key={pkg.id}
                    type="button"
                    className="glass-panel"
                    onClick={() => handlePackageSelect(pkg)}
                    style={{
                      padding: '0.45rem 0.58rem',
                      textAlign: 'left',
                      cursor: 'pointer',
                      borderRadius: '0.65rem',
                      border: isSelected ? '1px solid hsl(var(--accent-gold))' : '1px solid hsl(var(--surface-border) / 0.5)',
                      backgroundColor: isSelected ? 'hsl(var(--accent-gold) / 0.12)' : 'hsl(var(--surface-card) / 0.7)'
                    }}
                  >
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'hsl(var(--text-primary))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {isPeak ? 'Peak Season' : 'Off-Peak / Weekday'}
                    </div>
                    <div className="num-tabular" style={{ fontWeight: 800, color: isPeak ? 'hsl(var(--accent-gold))' : 'hsl(var(--accent-emerald))', fontSize: '0.8rem', marginTop: '0.1rem' }}>
                      Rs. {pkg.pricePkr.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '0.6rem', color: 'hsl(var(--text-muted))', marginTop: '0.06rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      AC • Gen • Valet • Stage
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Interactive Hall Add-ons */}
            <div style={{ borderTop: '1px solid hsl(var(--surface-border) / 0.5)', paddingTop: '0.42rem' }}>
              <div className="corridor-filter-strip" style={{ marginBottom: 0, padding: '0.04rem 0', gap: '0.3rem' }}>
                <button
                  type="button"
                  className={`corridor-chip ${addonFloralStage ? 'active' : ''}`}
                  style={{ fontSize: '0.65rem', padding: '0.2rem 0.52rem', flexShrink: 0 }}
                  onClick={() => setAddonFloralStage((v) => !v)}
                >
                  <span>Floral Stage (+55k)</span>
                </button>
                <button
                  type="button"
                  className={`corridor-chip ${addonValetTeam ? 'active' : ''}`}
                  style={{ fontSize: '0.65rem', padding: '0.2rem 0.52rem', flexShrink: 0 }}
                  onClick={() => setAddonValetTeam((v) => !v)}
                >
                  <span>Valet Team (+25k)</span>
                </button>
                <button
                  type="button"
                  className={`corridor-chip ${addonSoundLight ? 'active' : ''}`}
                  style={{ fontSize: '0.65rem', padding: '0.2rem 0.52rem', flexShrink: 0 }}
                  onClick={() => setAddonSoundLight((v) => !v)}
                >
                  <span>Sound &amp; Light (+35k)</span>
                </button>
              </div>
            </div>
          </div>

          {/* Live Estimated Budget Card */}
          <ConicGlowCard glowIntensity={0.75}>
            <div style={{ padding: '0.68rem 0.88rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem', gap: '0.5rem' }}>
                <div>
                  <span style={{ fontSize: '0.62rem', color: 'hsl(var(--text-muted))', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block' }}>
                    Total Estimated Rental ({eventDate} • {selectedSlot.toUpperCase()})
                  </span>
                  <span className="num-tabular" style={{ fontSize: '1.08rem', fontWeight: 800, color: 'hsl(var(--accent-gold))' }}>
                    Rs. {totalEstimatedPkr.toLocaleString()} PKR
                  </span>
                </div>
                <span className="pill-badge verified" style={{ fontSize: '0.6rem', padding: '0.12rem 0.42rem', flexShrink: 0 }}>
                  <CheckIcon size={10} /> Hold Ready
                </span>
              </div>

              <button
                type="button"
                className="btn-primary"
                style={{ width: '100%', height: '2.35rem', fontSize: '0.8rem' }}
                onClick={handleProceed}
              >
                <span>Proceed to 2-Step Confirmation Gate</span>
                <ArrowRightIcon size={14} />
              </button>
            </div>
          </ConicGlowCard>
        </div>
      </div>
    </div>
  );
};
